import { useState, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileWarning, AlertTriangle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export interface NormalizedCsvPreview {
  errors: Array<{ row: number; message: string }>;
  staleWarning: string | null;
  deletionWarning: string | null;
  hasChanges: boolean;
}

interface CsvImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  accept?: string;
  onPreview: (file: File) => Promise<NormalizedCsvPreview>;
  onCommit: (file: File) => Promise<void>;
  diffSummary: (preview: NormalizedCsvPreview) => ReactNode;
  helpContent?: ReactNode;
  commitLabel?: string;
}

export function CsvImportDialog({
  open,
  onOpenChange,
  title,
  description,
  accept = ".csv,text/csv",
  onPreview,
  onCommit,
  diffSummary,
  helpContent,
  commitLabel = "Import",
}: CsvImportDialogProps) {
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<NormalizedCsvPreview | null>(null);
  const [committing, setCommitting] = useState(false);
  const [deletionAcknowledged, setDeletionAcknowledged] = useState(false);

  function reset() {
    setSelectedFile(null);
    setPreviewing(false);
    setPreview(null);
    setCommitting(false);
    setDeletionAcknowledged(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreview(null);
    setDeletionAcknowledged(false);
    setPreviewing(true);
    try {
      const result = await onPreview(file);
      setPreview(result);
    } catch (err: any) {
      toast({
        title: "Preview failed",
        description: err.message || "Could not connect to server",
        variant: "destructive",
      });
    } finally {
      setPreviewing(false);
    }
  }

  async function handleCommit() {
    if (!selectedFile) return;
    setCommitting(true);
    try {
      await onCommit(selectedFile);
      handleOpenChange(false);
    } catch (err: any) {
      toast({
        title: "Import failed",
        description: err.message || "Could not connect to server",
        variant: "destructive",
      });
    } finally {
      setCommitting(false);
    }
  }

  const commitDisabled =
    !preview ||
    preview.errors.length > 0 ||
    committing ||
    (!!preview.deletionWarning && !deletionAcknowledged);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-import-csv">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>CSV File</Label>
            <Input
              type="file"
              accept={accept}
              onChange={handleFileChange}
              data-testid="input-import-file"
            />
          </div>

          {previewing && (
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground py-4"
              data-testid="import-preview-loading"
            >
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyzing CSV...
            </div>
          )}

          {preview && (
            <div className="space-y-3" data-testid="import-preview-results">
              {preview.errors.length > 0 && (
                <div
                  className="rounded-md border border-destructive/50 bg-destructive/10 p-3 space-y-1"
                  data-testid="import-errors"
                >
                  <div className="flex items-center gap-2 font-medium text-destructive text-sm">
                    <FileWarning className="w-4 h-4" />
                    {preview.errors.length} validation error{preview.errors.length !== 1 ? "s" : ""}
                  </div>
                  <ul className="text-sm text-destructive/90 list-disc list-inside space-y-0.5">
                    {preview.errors.slice(0, 10).map((err, i) => (
                      <li key={i}>Row {err.row}: {err.message}</li>
                    ))}
                    {preview.errors.length > 10 && (
                      <li>...and {preview.errors.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}

              {preview.staleWarning && (
                <div
                  className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3"
                  data-testid="import-stale-warning"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    Stale Export Warning
                  </div>
                  <p className="text-sm text-amber-600 dark:text-amber-400/80 mt-1">
                    {preview.staleWarning}
                  </p>
                </div>
              )}

              {preview.deletionWarning && (
                <div
                  className="rounded-md border border-destructive/50 bg-destructive/10 p-3"
                  data-testid="import-deletion-warning"
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                    <AlertTriangle className="w-4 h-4" />
                    High Deletion Count
                  </div>
                  <p className="text-sm text-destructive/80 mt-1">{preview.deletionWarning}</p>
                  <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deletionAcknowledged}
                      onChange={(e) => setDeletionAcknowledged(e.target.checked)}
                      data-testid="checkbox-acknowledge-deletion"
                    />
                    I understand and want to proceed
                  </label>
                </div>
              )}

              {preview.errors.length === 0 && (
                <div className="space-y-2" data-testid="import-diff-summary">
                  {diffSummary(preview)}
                </div>
              )}
            </div>
          )}
        </div>

        {helpContent && (
          <details className="text-sm border rounded-md" data-testid="import-help-collapsible">
            <summary className="cursor-pointer px-3 py-2 font-medium text-muted-foreground select-none">
              How does CSV import work?
            </summary>
            <div className="px-3 pb-3 pt-1 space-y-2 text-muted-foreground">
              {helpContent}
            </div>
          </details>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            data-testid="button-cancel-import"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCommit}
            disabled={commitDisabled}
            data-testid="button-commit-import"
          >
            {committing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              commitLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
